import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { CustomThemeService } from '../../../core/services/custom-theme.service';
import { ButtonStyleConfig, ThemeConfig } from '../../../core/interfaces/theme-config.interface';
import { ButtonComponent } from '../button/button.component';
import { ButtonSize, ButtonStyle } from '../../enums/button.enum';
import { AmountSelectorComponent } from '../controllers/amount-selector/amount-selector.component';

type TabType = 'text' | 'buttons' | 'other';

@Component({
  selector: 'app-theme-toolbar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    AmountSelectorComponent
  ],
  templateUrl: './theme-toolbar.component.html',
  styleUrls: ['./theme-toolbar.component.scss']
})
export class ThemeToolbarComponent implements OnInit, OnDestroy {
  private readonly themeService = inject(CustomThemeService);
  private readonly destroy$ = new Subject<void>();
  private readonly themeChange$ = new Subject<void>();

  isCollapsed = true;
  showCopiedMessage = false;
  messageText = '';
  messageType: 'success' | 'error' = 'success';
  activeTab: TabType = 'text';

  themeConfig: ThemeConfig | null = null;

  protected readonly ButtonStyle = ButtonStyle;
  protected readonly ButtonSize = ButtonSize;

  // Button style keys for iteration
  buttonStyleKeys: Array<keyof ThemeConfig['buttonStyles']> = [
    'primary',
    'secondary',
    'contrast',
    'transparent',
    'danger'
  ];

  // Button size keys for iteration
  buttonSizeKeys: Array<keyof ThemeConfig['buttonSizes']> = [
    'small',
    'medium',
    'big',
    'large',
    'icon'
  ];

  ngOnInit(): void {
    this.themeService.currentTheme$
      .pipe(takeUntil(this.destroy$))
      .subscribe((theme) => {
        if (theme) {
          this.themeConfig = JSON.parse(JSON.stringify(theme));
        }
      });

    this.themeChange$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.applyChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.themeChange$.complete();
  }

  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  setActiveTab(tab: TabType): void {
    this.activeTab = tab;
  }

  onColorChange(colorKey: keyof ThemeConfig['colors'], value: string): void {
    if (!this.themeConfig) return;

    this.themeConfig.colors[colorKey] = value;
    this.triggerThemeChange();
  }

  onButtonStyleChange(
    styleName: keyof ThemeConfig['buttonStyles'],
    property: keyof ButtonStyleConfig,
    value: string
  ): void {
    if (!this.themeConfig) return;

    this.themeConfig.buttonStyles[styleName][property] = value;
    this.triggerThemeChange();
  }

  onButtonSizeChange(
    sizeName: keyof ThemeConfig['buttonSizes'],
    value: string
  ): void {
    if (!this.themeConfig) return;

    this.themeConfig.buttonSizes[sizeName].borderRadius = value;
    this.triggerThemeChange();
  }

  resetToDefault(): void {
    this.themeService.resetToDefault();
    this.showTemporaryMessage('Theme reset to default!');
  }

  copyJsonConfig(): void {
    const json = this.themeService.exportThemeAsJson();

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(json)
        .then(() => {
          this.showTemporaryMessage('JSON config copied to clipboard!');
        })
        .catch((err) => {
          console.error('Failed to copy to clipboard:', err);
          this.fallbackCopyToClipboard(json);
        });
    } else {
      this.fallbackCopyToClipboard(json);
    }
  }

  async pasteJsonConfig(): Promise<void> {
    try {
      const json = await navigator.clipboard.readText();

      // Trim whitespace and check if it's a valid JSON string
      const trimmedJson = json.trim();

      if (!trimmedJson) {
        this.showTemporaryMessage('Clipboard is empty!', 'error');
        return;
      }

      try {
        const config: ThemeConfig = JSON.parse(trimmedJson);

        // Validate the structure
        if (config.colors && config.buttonStyles && config.buttonSizes) {
          this.themeConfig = config;
          this.applyChanges();
          this.showTemporaryMessage('JSON config pasted and applied!');
        } else {
          this.showTemporaryMessage(
            'Invalid JSON structure! Missing required fields.',
            'error'
          );
          console.error('Invalid theme config structure:', config);
        }
      } catch (parseError) {
        this.showTemporaryMessage(
          'Invalid JSON format! Check clipboard content.',
          'error'
        );
        console.error('Failed to parse JSON:', parseError);
        console.error('Clipboard content:', trimmedJson.substring(0, 100));
      }
    } catch (err) {
      console.error('Failed to read from clipboard:', err);
      this.showTemporaryMessage('Failed to read clipboard!', 'error');
    }
  }

  getButtonStyleLabel(key: string): string {
    const labels: Record<string, string> = {
      primary: 'Primary',
      secondary: 'Secondary',
      contrast: 'Contrast',
      transparent: 'Transparent',
      danger: 'Danger'
    };
    return labels[key] || key;
  }

  getButtonSizeLabel(key: string): string {
    const labels: Record<string, string> = {
      small: 'Small',
      medium: 'Medium',
      big: 'Big',
      large: 'Large',
      icon: 'Icon'
    };
    return labels[key] || key;
  }

  getButtonStyleEnum(styleKey: string): ButtonStyle {
    const styleMap: Record<string, ButtonStyle> = {
      primary: ButtonStyle.Primary,
      secondary: ButtonStyle.Secondary,
      contrast: ButtonStyle.Contrast,
      transparent: ButtonStyle.Transparent,
      danger: ButtonStyle.Danger
    };
    return styleMap[styleKey] || ButtonStyle.Primary;
  }

  getButtonSizeEnum(sizeKey: string): ButtonSize {
    const sizeMap: Record<string, ButtonSize> = {
      small: ButtonSize.Small,
      medium: ButtonSize.Medium,
      big: ButtonSize.Big,
      large: ButtonSize.Large,
      icon: ButtonSize.Icon
    };
    return sizeMap[sizeKey] || ButtonSize.Medium;
  }

  private fallbackCopyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();

    try {
      document.execCommand('copy');
      this.showTemporaryMessage('JSON config copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      this.showTemporaryMessage(
        'Failed to copy. Please copy manually from console.',
        'error'
      );
      console.log('Theme Config:', text);
    } finally {
      document.body.removeChild(textArea);
    }
  }

  private triggerThemeChange(): void {
    this.themeChange$.next();
  }

  private applyChanges(): void {
    if (this.themeConfig) {
      this.themeService.applyTheme(this.themeConfig);
    }
  }

  private showTemporaryMessage(message: string, type: 'success' | 'error' = 'success'): void {
    this.messageText = message;
    this.messageType = type;
    this.showCopiedMessage = true;
    setTimeout(() => (this.showCopiedMessage = false), 3000);
  }
}
